import { Collateral, DealDetail, DealStatus, NegotiationFieldMeta, PlatformFeature, ProductSector, ServicingType, TradingType } from 'api'
import { getNegotiatedValue } from 'model'
import { ReadAccess } from 'utils'
import { TypedEditableCallbackParams } from 'view/components/datagrid'
import { canServicingUpdateAttributes } from 'view/routes/deals/components/utils'

type MidTradeEventEditableFields =
  | NegotiationFieldMeta.NOTIONAL
  | NegotiationFieldMeta.RATE
  | NegotiationFieldMeta.HAIRCUT
  | NegotiationFieldMeta.HAIRCUT_TYPE
  | NegotiationFieldMeta.START_DATE
  | NegotiationFieldMeta.START_CONSIDERATION
  | NegotiationFieldMeta.BOND_PRICE
  | NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE
  | NegotiationFieldMeta.SECONDARY_BASKET_ID
  | NegotiationFieldMeta.FEE

export function makeCanEditMidTradeEventDetailField(field: MidTradeEventEditableFields) {
  return function canEditMidTradeEventDetailField(params: TypedEditableCallbackParams<DealDetail>) {
    return (
      !!params.data &&
      canServicingUpdateAttributes(params.data) &&
      params.data.servicingSideDetails?.negotiationFields[field]?.fieldType === ReadAccess.READ_WRITE
    )
  }
}

export function canEditMidTradeEventDetailNotional(params: TypedEditableCallbackParams<DealDetail>) {
  if (!makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.NOTIONAL)(params)) {
    return false
  }

  const tradingType =
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.TRADING_TYPE]?.negotiableFieldRequired) ??
    params.data?.deal.tradingType
  return tradingType === TradingType.NOTIONAL
}

export function canEditMidTradeEventDetailStartConsideration(params: TypedEditableCallbackParams<DealDetail>) {
  if (!makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.START_CONSIDERATION)(params)) {
    return false
  }

  const tradingType =
    getNegotiatedValue(params.data?.servicingSideDetails?.negotiationFields[NegotiationFieldMeta.TRADING_TYPE]?.negotiableFieldRequired) ??
    params.data?.deal.tradingType

  return tradingType !== TradingType.NOTIONAL
}

export function canEditMidTradeEventDetailStartConsiderationOverride(params: TypedEditableCallbackParams<DealDetail>) {
  if (!makeCanEditMidTradeEventDetailField(NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE)(params) || !params.data) {
    return false
  }

  return (
    canEditMidTradeEventDetailStartConsideration(params) &&
    params.data.productSector?.startConsiderationOverrideThreshold !== 0 &&
    counterpartyOrganisationStartConsiderationOverrideEnabled(params.data)
  )
}

export function canEditMidTradeEventDetailEffectiveDate(params: TypedEditableCallbackParams<DealDetail>) {
  if (!params.data) {
    return false
  }

  const {
    deal: { servicingDetails, status },
    servicingSideDetails
  } = params.data

  if (!canServicingUpdateAttributes(params.data)) {
    return false
  }

  switch (servicingDetails?.servicingType?.servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return (
        servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.fieldType === ReadAccess.READ_WRITE ||
        (servicingSideDetails?.negotiationFields[NegotiationFieldMeta.END_DATE]?.fieldType === ReadAccess.INITIATOR_WRITE_RESPONDER_READ &&
          status === DealStatus.SERVICING_WITH_INITIATOR)
      )
    case ServicingType.RERATE:
      return servicingSideDetails?.negotiationFields[NegotiationFieldMeta.RERATE_DATE]?.fieldType === ReadAccess.READ_WRITE
    default:
      return servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_DATE]?.fieldType === ReadAccess.READ_WRITE
  }
}

export function canEditMidTradeEventCloseOpenDetailStartConsiderationOverride(params: DealDetail) {
  return (
    params.collateral?.tradingType === TradingType.NOTIONAL ||
    params.collateralBasket?.tradingType === TradingType.NOTIONAL ||
    params.productSector?.startConsiderationOverrideThreshold === 0 ||
    !counterpartyOrganisationStartConsiderationOverrideEnabled(params)
  )
}

export function canEditMidTradeEventSubstitutionDetailStartConsiderationOverride(
  params: DealDetail,
  newCollateral: Collateral | null,
  productSector: ProductSector | null
) {
  if (!newCollateral || !productSector) {
    return true
  }

  return (
    newCollateral.tradingType === TradingType.NOTIONAL ||
    productSector.startConsiderationOverrideThreshold === 0 ||
    !counterpartyOrganisationStartConsiderationOverrideEnabled(params)
  )
}

export function counterpartyOrganisationStartConsiderationOverrideEnabled(params: DealDetail) {
  return params.ownOrder
    ? !!params.counterpartyFirm?.enabledFeatures.includes(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)
    : !!params.orderFundFirm?.enabledFeatures.includes(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)
}

export function canEditTraderAmendStartConsiderationOverride(params: DealDetail) {
  return (
    params.deal.tradingType === TradingType.NOTIONAL ||
    params.productSector?.startConsiderationOverrideThreshold === 0 ||
    !counterpartyOrganisationStartConsiderationOverrideEnabled(params)
  )
}
